import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Check } from 'lucide-react';

const SovereignApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    howDidYouHear: '',
    familyResponsibility: '',
    exposureAwareness: '',
    experience: '',
    decisionStyle: '',
    intent: '',
    expectations: '',
    readiness: '',
    anythingElse: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/sovereign/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] pt-24 pb-20">
        <div className="container px-6 sm:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-[var(--bg-secondary)] border border-[var(--gold-primary)] rounded-full">
              <Check size={40} className="text-[var(--gold-primary)]" />
            </div>
            <p className="body-large text-[var(--text-primary)] mb-6 leading-relaxed">
              Thank you for your thoughtful application.
            </p>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              Your details have been received and will be reviewed personally.
            </p>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              If alignment exists, you will be contacted directly.
            </p>
            <p className="body-small text-[var(--text-light)] mb-12 italic">
              This process respects your time — and mine.
            </p>
            <Link to="/" className="btn-primary">
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Black Premium Hero Section */}
      <div className="bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] pt-32 pb-20 border-b border-[var(--gold-primary)]/20">
        <div className="container px-6 sm:px-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Shield Icon */}
            <div className="w-16 h-16 mx-auto mb-8 flex items-center justify-center">
              <Shield size={40} className="text-[var(--gold-primary)]" />
            </div>
            
            {/* Hero Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4 text-white tracking-tight">
              Family Protection Sovereign™
            </h1>
            <p className="text-sm uppercase tracking-wider text-[var(--gold-primary)] mb-10 font-medium">
              A Private Stewardship by Invitation Only
            </p>
            
            {/* Hero Subtitle */}
            <p className="text-lg sm:text-xl leading-relaxed text-gray-300 max-w-2xl mx-auto">
              For families who don't want advice, reviews, or documents —<br className="hidden sm:block" />
              but want one accountable protection authority.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-6 sm:px-10 py-20">
        <div className="max-w-3xl mx-auto">
          
          {/* Authority Definition Block - Premium Center Card */}
          <section className="mb-20">
            <div className="max-w-[760px] mx-auto">
              <div className="bg-[var(--bg-secondary)] rounded-2xl p-10 sm:p-12 text-center border border-[var(--gold-primary)]/10 shadow-sm">
                {/* Top Gold Divider */}
                <div className="w-16 h-px bg-[var(--gold-primary)] mx-auto mb-8"></div>
                
                <p className="text-[17px] leading-[1.8] text-[#1a1a1a] mb-6">
                  Family Protection Sovereign™ is not advice.<br />
                  It is not a session.<br />
                  It is not a document or review.
                </p>
                
                <p className="text-[17px] leading-[1.8] text-[#2a2a2a] mb-6">
                  It is long-term protection stewardship for a very limited number of families<br className="hidden sm:block" />
                  who want clarity, continuity, and calm authority —<br className="hidden sm:block" />
                  especially when life becomes uncertain.
                </p>
                
                <p className="text-[15px] leading-[1.8] text-[#4a4a4a] italic">
                  Applications are reviewed personally.<br />
                  This is not available by request.
                </p>
                
                {/* Bottom Gold Divider */}
                <div className="w-16 h-px bg-[var(--gold-primary)] mx-auto mt-8"></div>
              </div>
            </div>
          </section>

          {/* Who This Is For */}
          <section className="mb-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="heading-2 text-center mb-10">Who This Is For</h2>
              <p className="body-regular text-[var(--text-secondary)] text-center mb-8">
                This stewardship is designed for families who:
              </p>
              
              <ul className="space-y-4 mb-10 max-w-xl mx-auto">
                {[
                  'Carry significant responsibility (dependents, assets, cross-border life)',
                  'Think in decades, not transactions',
                  'Prefer delegation after clarity, not constant decision-making',
                  'Value discretion, structure, and continuity',
                  'Understand protection as responsibility — not a product'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 mt-2.5 bg-[var(--gold-primary)] rounded-full flex-shrink-0"></span>
                    <span className="body-regular text-[var(--text-secondary)]">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-[var(--bg-secondary)] p-6 sm:p-8 border-l-2 border-[var(--gold-primary)]">
                <p className="body-regular text-[var(--text-secondary)] italic leading-relaxed text-center">
                  If you are looking for quotes, comparisons, or quick answers,<br className="hidden sm:block" />
                  this is not the right place.
                </p>
              </div>
            </div>
          </section>

          {/* Why Sovereign Exists */}
          <section className="mb-16">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="heading-2 mb-10">Why Sovereign™ Exists</h2>
              
              <div className="space-y-6">
                <p className="body-regular text-[var(--text-secondary)] leading-relaxed">
                  Families rarely fail because they lacked policies.<br />
                  They fail because no one was accountable<br />
                  when decisions, claims, or disruptions occurred.
                </p>
                <p className="body-regular text-[var(--text-secondary)] leading-relaxed">
                  Sovereign™ exists so a family never navigates uncertainty alone —<br />
                  or depends on agents, portals, or fragmented opinions<br />
                  when clarity matters most.
                </p>
              </div>
            </div>
          </section>

          {/* About This Application */}
          <section className="mb-16">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="heading-2 mb-10">About This Application</h2>
              
              <div className="space-y-6">
                <p className="body-regular text-[var(--text-primary)] leading-relaxed">
                  This application is not a formality.
                </p>
                <p className="body-regular text-[var(--text-secondary)] leading-relaxed">
                  It helps determine mutual alignment —<br />
                  between your family's responsibility<br />
                  and my stewardship philosophy.
                </p>
                <p className="body-small text-[var(--text-light)] italic leading-relaxed">
                  Not all applications are accepted.<br />
                  No follow-ups are sent if alignment does not exist.
                </p>
              </div>
            </div>
          </section>

          {/* Application Form */}
          <section className="mb-16">
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
              <div className="space-y-8">
                
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-6">Personal Information</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-[var(--text-secondary)] mb-2">Country *</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          required
                          className="sovereign-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[var(--text-secondary)] mb-2">State / Region *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          className="sovereign-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">How did you hear about Sovereign™? *</label>
                      <input
                        type="text"
                        name="howDidYouHear"
                        value={formData.howDidYouHear}
                        onChange={handleChange}
                        required
                        className="sovereign-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Application Questions */}
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-6">Application Questions</h3>
                  <div className="space-y-6">
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        Briefly describe the protection responsibility you carry for your family *
                      </label>
                      <textarea
                        name="familyResponsibility"
                        value={formData.familyResponsibility}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        What is your primary concern or exposure that you want clarity on? *
                      </label>
                      <textarea
                        name="exposureAwareness"
                        value={formData.exposureAwareness}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        Have you experienced a protection-related crisis or claim in the past? If yes, briefly explain *
                      </label>
                      <textarea
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-3">
                        How do you prefer to make significant protection decisions? *
                      </label>
                      <div className="space-y-3">
                        {[
                          { value: 'independent', label: 'I prefer to research and decide independently' },
                          { value: 'guided', label: 'I want guidance, but I make the final call' },
                          { value: 'delegated', label: 'I prefer delegated authority after initial clarity' }
                        ].map((option) => (
                          <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name="decisionStyle"
                              value={option.value}
                              checked={formData.decisionStyle === option.value}
                              onChange={handleChange}
                              required
                              className="sovereign-radio"
                            />
                            <span className="text-sm text-[var(--text-secondary)]">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        Why are you considering Sovereign™ specifically? *
                      </label>
                      <textarea
                        name="intent"
                        value={formData.intent}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        What does "peace of mind" mean to you in the context of family protection? *
                      </label>
                      <textarea
                        name="expectations"
                        value={formData.expectations}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="sovereign-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-3">
                        Are you prepared for long-term stewardship (vs. a one-time consultation)? *
                      </label>
                      <div className="space-y-3">
                        {[
                          { value: 'yes', label: 'Yes, I am looking for ongoing stewardship' },
                          { value: 'not-sure', label: 'Not sure, I need more clarity first' },
                          { value: 'no', label: 'No, I prefer one-time guidance' }
                        ].map((option) => (
                          <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name="readiness"
                              value={option.value}
                              checked={formData.readiness === option.value}
                              onChange={handleChange}
                              required
                              className="sovereign-radio"
                            />
                            <span className="text-sm text-[var(--text-secondary)]">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-[var(--text-secondary)] mb-2">
                        Is there anything you believe I should know before considering your application?
                      </label>
                      <textarea
                        name="anythingElse"
                        value={formData.anythingElse}
                        onChange={handleChange}
                        rows={3}
                        className="sovereign-input"
                        placeholder="Optional..."
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-8 pb-20">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold inline-flex items-center gap-2 min-w-[280px]"
                  >
                    <Shield size={18} />
                    {isSubmitting ? 'Submitting...' : 'Request Consideration'}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* What Happens Next */}
          <section className="mb-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="heading-2 text-center mb-10">What Happens Next</h2>
              
              <ul className="space-y-4 max-w-xl mx-auto">
                {[
                  'Applications are reviewed personally',
                  'Review time: 5–7 working days',
                  'If alignment exists, you will be contacted directly',
                  'If not, no follow-ups are sent'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 mt-2.5 bg-[var(--gold-primary)] rounded-full flex-shrink-0"></span>
                    <span className="body-regular text-[var(--text-secondary)]">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 text-center">
                <p className="body-small text-[var(--text-light)] italic">
                  This process respects your time — and mine.
                </p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default SovereignApplication;
